import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { HelmetProvider } from 'react-helmet-async';
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Index from "./pages/Index";
import Services from "./pages/Services";
import Gallery from "./pages/Gallery";
import Contact from "./pages/Contact";
import Booking from "./pages/Booking";
import BookingConfirmation from "./pages/BookingConfirmation";
import AreasWeServe from "./pages/AreasWeServe";
import Quote from "./pages/Quote";
import NotFound from "./pages/NotFound";
import { SEO } from "@/components/SEO";

// Lazy load city pages
import { Suspense, lazy } from "react";
const Minnetonka = lazy(() => import("./pages/cities/Minnetonka"));
const EdenPrairie = lazy(() => import("./pages/cities/EdenPrairie"));
const Plymouth = lazy(() => import("./pages/cities/Plymouth"));
const MapleGrove = lazy(() => import("./pages/cities/MapleGrove"));
const Osseo = lazy(() => import("./pages/cities/Osseo"));
const BrooklynPark = lazy(() => import("./pages/cities/BrooklynPark"));
const Edina = lazy(() => import("./pages/cities/Edina"));
const Hopkins = lazy(() => import("./pages/cities/Hopkins"));
const StLouisPark = lazy(() => import("./pages/cities/StLouisPark"));
const Crystal = lazy(() => import("./pages/cities/Crystal"));
const Minneapolis = lazy(() => import("./pages/cities/Minneapolis"));
const StPaul = lazy(() => import("./pages/cities/StPaul"));
const Burnsville = lazy(() => import("./pages/cities/Burnsville"));
const Mound = lazy(() => import("./pages/cities/Mound"));
const Excelsior = lazy(() => import("./pages/cities/Excelsior"));
const Rockford = lazy(() => import("./pages/cities/Rockford"));
const GoldenValley = lazy(() => import("./pages/cities/GoldenValley"));
const NewBrighton = lazy(() => import("./pages/cities/NewBrighton"));
const Wayzata = lazy(() => import("./pages/cities/Wayzata"));
const Orono = lazy(() => import("./pages/cities/Orono"));

// Lazy load new pages to match ecomobiledetailing.com structure
const About = lazy(() => import("./pages/About"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Blog = lazy(() => import("./pages/Blog"));

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// Configure QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Wrap lazy-loaded components with Suspense
const LazyRoute = ({ element: Element }: { element: React.ComponentType }) => (
  <Suspense fallback={<LoadingSpinner />}>
    <Element />
  </Suspense>
);

// Centralized SEO metadata for static routes (excluding those with manual JSON-LD)
const routeMeta: Record<string, { title: string; description: string; image?: string }> = {
  "/": {
    title: "Mobile Detail Co. | Professional Auto Detailing Services",
    description: "Expert mobile detailing services that come to your location. Eco-friendly car wash, interior detailing, ceramic coating, and more.",
    image: "/og-image.png",
  },
  "/about": {
    title: "About Us | Mobile Detail Co.",
    description: "Learn about Mobile Detail Co. - your trusted mobile detailing service with years of experience in car wash and automotive detailing.",
  },
  "/testimonials": {
    title: "Customer Testimonials | Mobile Detail Co.",
    description: "Read what our customers have to say about our professional mobile detailing services.",
  },
  "/faq": {
    title: "Frequently Asked Questions | Mobile Detail Co.",
    description: "Find answers to common questions about our mobile detailing services, including pricing, process, and what to expect.",
  },
  "/blog": {
    title: "Blog | Mobile Detail Co.",
    description: "Read our blog for tips, guides, and the latest news on automotive detailing and maintenance.",
  },
  "/contact": {
    title: "Contact Us | Mobile Detail Co.",
    description: "Contact Mobile Detail Co for appointments, questions, or quotes. We're here to help with all your car detailing needs in the Twin Cities area.",
  },
  "/booking": {
    title: "Book Auto Detailing Service | Mobile Detail Co.",
    description: "Schedule your professional mobile auto detailing appointment at your location. Fast, convenient, and eco-friendly service.",
  },
  "/booking/confirmation": {
    title: "Booking Request Received | Mobile Detail Co.",
    description: "Thank you! Your mobile detailing appointment request has been received. We will contact you to confirm the details.",
  },
  "/areas-we-serve": {
    title: "Areas We Serve | Mobile Detail Co.",
    description: "Discover all the Minneapolis and St. Paul suburbs where we offer our mobile car detailing services.",
  },
  "/services": {
    title: "Our Detailing Services | Mobile Detail Co.",
    description: "Explore our range of mobile auto detailing services, from exterior washes to full interior cleaning and ceramic coatings.",
  },
  "/gallery": {
    title: "Photo Gallery | Mobile Detail Co.",
    description: "See the results of our professional mobile detailing. Before and after photos of our car cleaning and restoration work.",
  },
  "/quote": {
    title: "Get a Detailing Quote | Mobile Detail Co.",
    description: "Request a free quote for our mobile auto detailing services. Tailored packages to meet your car care needs.",
  },
};

// Component to inject SEO metadata dynamically based on current path
function MetaDynamic() {
  const { pathname } = useLocation();
  const meta = routeMeta[pathname];
  if (!meta) return null;
  return <SEO {...meta} url={window.location.origin + pathname} />;
}

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <MetaDynamic />
          <div className="min-h-screen flex flex-col">
            <Header />
            <main className="flex-grow">
              <Routes>
                <Route path="/" element={<Index />} />
                {/* Redirect routes to homepage sections - NO, these are now standalone pages */}
                <Route path="/about" element={<LazyRoute element={About} />} />
                <Route path="/services" element={<Services />} />
                <Route path="/testimonials" element={<LazyRoute element={Testimonials} />} />
                <Route path="/faq" element={<LazyRoute element={FAQ} />} />
                <Route path="/areas-we-serve" element={<LazyRoute element={AreasWeServe} />} />
                <Route path="/blog" element={<LazyRoute element={Blog} />} />

                {/* Keep non-section pages */}
                <Route path="/contact" element={<Contact />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/booking" element={<Booking />} />
                <Route path="/booking/confirmation" element={<BookingConfirmation />} />
                <Route path="/quote" element={<Quote />} />
                
                {/* City pages remain separate */}
                <Route path="/minnetonka" element={<LazyRoute element={Minnetonka} />} />
                <Route path="/eden-prairie" element={<LazyRoute element={EdenPrairie} />} />
                <Route path="/plymouth" element={<LazyRoute element={Plymouth} />} />
                <Route path="/maple-grove" element={<LazyRoute element={MapleGrove} />} />
                <Route path="/osseo" element={<LazyRoute element={Osseo} />} />
                <Route path="/brooklyn-park" element={<LazyRoute element={BrooklynPark} />} />
                <Route path="/edina" element={<LazyRoute element={Edina} />} />
                <Route path="/hopkins" element={<LazyRoute element={Hopkins} />} />
                <Route path="/st-louis-park" element={<LazyRoute element={StLouisPark} />} />
                <Route path="/crystal" element={<LazyRoute element={Crystal} />} />
                <Route path="/minneapolis" element={<LazyRoute element={Minneapolis} />} />
                <Route path="/st-paul" element={<LazyRoute element={StPaul} />} />
                <Route path="/burnsville" element={<LazyRoute element={Burnsville} />} />
                <Route path="/mound" element={<LazyRoute element={Mound} />} />
                <Route path="/excelsior" element={<LazyRoute element={Excelsior} />} />
                <Route path="/rockford" element={<LazyRoute element={Rockford} />} />
                <Route path="/golden-valley" element={<LazyRoute element={GoldenValley} />} />
                <Route path="/new-brighton" element={<LazyRoute element={NewBrighton} />} />
                <Route path="/wayzata" element={<LazyRoute element={Wayzata} />} />
                <Route path="/orono" element={<LazyRoute element={Orono} />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            <Footer />
          </div>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
